import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { guestGuard } from './core/guards/guest-guard';

export const routes: Routes = [
  // AUTH ROUTES
  {
    path: '',
    loadComponent: () => import('./auth/auth').then((m) => m.Auth),
    canActivate: [guestGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./auth/pages/login/login').then((m) => m.Login),
      },
      {
        path: 'registration-confirmation/:token',
        loadComponent: () =>
          import('./auth/pages/confirm-registration/confirm-registration').then(
            (m) => m.ConfirmRegistration,
          ),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./auth/pages/forgot-password-init/forgot-password-init').then(
            (m) => m.ForgotPasswordInit,
          ),
      },
      {
        path: 'forgot-password-process/:token',
        loadComponent: () =>
          import('./auth/pages/forgot-password-process/forgot-password-process').then(
            (m) => m.ForgotPasswordProcess,
          ),
      },

      {
        path: ':lang/internal-user-login/:crmToken/:objectId',
        loadComponent: () =>
          import('./auth/pages/internal-user-login/internal-user-login').then(
            (m) => m.InternalUserLogin,
          ),
      },
      {
        path: 'internal-user-login/:crmToken/:objectId',
        loadComponent: () =>
          import('./auth/pages/internal-user-login/internal-user-login').then(
            (m) => m.InternalUserLogin,
          ),
      },
    ],
  },

  // USER ROUTES
  {
    path: 'user',
    loadComponent: () => import('./user/user').then((m) => m.User),
    canActivate: [authGuard],
    children: [
      {
        path: 'user-data',
        loadComponent: () => import('./user/pages/user-data/user-data').then((m) => m.UserData),
      },
      {
        path: 'company-about',
        loadComponent: () =>
          import('./user/pages/company-about/company-about').then((m) => m.CompanyAbout),
      },

      // OBJECT ROUTES
      // requires: hasCompanyGuard (canActivate)
      {
        path: 'object',
        loadComponent: () => import('./object/object').then((m) => m.Object),
        // canActivate: [hasCompanyGuard],
        children: [
          {
            path: '',
            loadComponent: () => import('./object/pages/list/list').then((m) => m.List),
          },
          {
            path: 'new',
            loadComponent: () => import('./object/pages/new/new').then((m) => m.New),
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./object/pages/new/new').then((m) => m.New),
          },
          // OBJECT VARIATION ROUTES
          {
            path: 'planning',
            loadComponent: () => import('./object/pages/planning/planning').then((m) => m.Planning),
            children: [
              {
                path: ':objectId/variation/new',
                loadComponent: () =>
                  import('./object/pages/planning/variation/new/new').then((m) => m.New),
              },
              {
                path: ':objectId/variation/edit/:objectVariationId',
                loadComponent: () =>
                  import('./object/pages/planning/variation/new/new').then((m) => m.New),
              },
              // MWOD MODE
              {
                path: ':objectId/variation/:objectVariationId/mwod/subsystem/:subsystemId',
                loadComponent: () =>
                  import('./object/pages/planning/variation/mwod/mwod').then((m) => m.Mwod),
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./object/pages/planning/variation/mwod/mwod-form/mwod-form').then(
                        (m) => m.MwodForm,
                      ),
                    data: { reuseRoute: false },
                  },
                ],
                data: { reuseRoute: false },
              },
              // EXPERT MODE
              {
                path: ':objectId/variation/:objectVariationId/expert',
                loadComponent: () =>
                  import('./object/pages/planning/variation/expert/expert').then((m) => m.Expert),
                children: [
                  // Expert form (default)
                  {
                    path: '',
                    loadComponent: () =>
                      import('./object/pages/planning/variation/expert/expert-form/expert-form').then(
                        (m) => m.ExpertForm,
                      ),
                  },
                  // Subsystem: Detention
                  {
                    path: 'subsystem/:subsystemId/detention',
                    loadComponent: () =>
                      import('./object/pages/planning/variation/expert/subsystems/detention/detention').then(
                        (m) => m.Detention,
                      ),
                  },
                  // Subsystem: Infiltration
                  {
                    path: 'subsystem/:subsystemId/infiltration',
                    loadComponent: () =>
                      import('./object/pages/planning/variation/expert/subsystems/infiltration/infiltration').then(
                        (m) => m.Infiltration,
                      ),
                  },
                  // Subsystem: Infiltration of rainwater
                  {
                    path: 'subsystem/:subsystemId/infiltration-of-rainwater',
                    loadComponent: () =>
                      import('./object/pages/planning/variation/expert/subsystems/infiltration-of-rainwater/infiltration-of-rainwater').then(
                        (m) => m.InfiltrationOfRainwater,
                      ),
                  },
                  // Subsystem: Treatment
                  {
                    path: 'subsystem/:subsystemId/treatment',
                    loadComponent: () =>
                      import('./object/pages/planning/variation/expert/subsystems/treatment/treatment').then(
                        (m) => m.Treatment,
                      ),
                  },
                  // Subsystem: Proof of overflow
                  {
                    path: 'subsystem/:subsystemId/proof-of-overflow',
                    loadComponent: () =>
                      import('./object/pages/planning/variation/expert/subsystems/proof-of-overflow/proof-of-overflow').then(
                        (m) => m.ProofOfOverflow,
                      ),
                  },
                ],
              },
              // ──────────────────────────────────
              // GPM MODE
              // ──────────────────────────────────
              {
                path: ':objectId/variation/:objectVariationId/gpm',
                loadComponent: () =>
                  import('./object/pages/planning/variation/gpm/gpm').then((m) => m.Gpm),
              },
              {
                path: ':objectId/variation/:objectVariationId/gpm/subsystem/:subsystemId',
                loadComponent: () =>
                  import('./object/pages/planning/variation/gpm/gpm').then((m) => m.Gpm),
              },
              // ──────────────────────────────────
              // MATERIAL LIST
              // ──────────────────────────────────
              {
                path: ':objectId/variation/:objectVariationId/material-list',
                loadComponent: () =>
                  import('./object/pages/planning/variation/material-list/material-list').then(
                    (m) => m.MaterialList,
                  ),
              },
              {
                path: ':objectId/variation/:objectVariationId/material-list/subsystem/:subsystemId',
                loadComponent: () =>
                  import('./object/pages/planning/variation/material-list/material-list').then(
                    (m) => m.MaterialList,
                  ),
              },
            ],
          },
        ],
      },
      // Catch-all: not found
      {
        path: '**',
        loadComponent: () =>
          import('./user/pages/page-not-found/page-not-found').then((m) => m.PageNotFound),
      },
    ],
  },

  // SHARED ROUTES
  {
    path: 'about',
    loadComponent: () => import('./shared/pages/about/about').then((m) => m.About),
  },
  {
    path: 'terms-of-use',
    loadComponent: () =>
      import('./shared/pages/terms-of-use/terms-of-use').then((m) => m.TermsOfUse),
  },

  // TEST ROUTE
  {
    path: 'test',
    loadComponent: () => import('./test/test').then((m) => m.Test),
  },

  // WILDCARD: REDIRECT TO HOMEPAGE
  { path: '**', redirectTo: '', pathMatch: 'full' },
];
